import React, { ReactElement, useContext, createRef, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
    Drawer, Form, notification, Select, Space, Button, Input, InputNumber,
    Checkbox, Switch
} from 'antd'
import { SmileOutlined } from '@ant-design/icons'
import type { CheckboxChangeEvent } from 'antd/es/checkbox'
import type { FormInstance } from 'antd/es/form'
import type { IAppState } from '@/store/reducers'
import { useTranslation } from 'react-i18next'
import { DrawerShowContext } from '@/pages/table2'
import { getProjectInfoList, setKeyRoadMapPagination, setProjectInfoPagination } from '@/store/actions/tableEdit.action'

function Table2SearchPop (): ReactElement {
    const dispatch = useDispatch()
    const { t, i18n } = useTranslation()
    const {
        showSearchPop,
        setShowSearchPop,
        searchObj,
        setSearchObj,
        classArr,
        curWorkgroup,
        prodList,
        buList
    } = useContext(DrawerShowContext)
    const dicClassList = useSelector((state: IAppState) => state.tableEditData.dicClass)
    const paginationInfo = useSelector((state: IAppState) => state.tableEditData.projectInfoPagination)
    const [maxResourceNeeded, setMaxResourceNeeded] = useState(0)
    const [maxResourceNeededFlag, setMaxResourceNeededFlag] = useState(false)
    const [minResourceNeeded, setMinResourceNeeded] = useState(0)
    const [minResourceNeededFlag, setMinResourceNeededFlag] = useState(false)
    const formRef = createRef<FormInstance>()
    const { Option } = Select
    const layout = {
        labelCol: { span: 8 },
        wrapperCol: { span: 16 },
    }
    const tailLayout = {
        wrapperCol: { offset: 8, span: 10 },
    }
    const onClose = () => {
        setShowSearchPop(false)
    }
    const onFinish = async (formData: any) => {
        const values = {
            ...formData,
            buStakeholder: formData.buStakeholder ? formData.buStakeholder.join(',') : null,
            maxResourceNeeded: maxResourceNeededFlag ? maxResourceNeeded : undefined,
            minResourceNeeded: minResourceNeededFlag ? minResourceNeeded : undefined
        }
        console.log('values: ', values)
        setSearchObj(values)
        dispatch(getProjectInfoList({
            ...values,
            tMTApproval: values.tmtapproval,
            pageNo: 1,
            pageSize: paginationInfo.pageSize,
            workGroupId: curWorkgroup?.id
        }))
        dispatch(setKeyRoadMapPagination({
            pageIndex: 1
        }))
        dispatch(setProjectInfoPagination({
            pageIndex: 1,
            pageSize: paginationInfo.pageSize,
        }))
        onClose()
    }
    const onFinishFailed = ({ errorFields }: { errorFields: any}) => {
        if (errorFields.length) {
            const args = {
                message: t('FinishFailed'),
                description: '',
                icon: <SmileOutlined style={{ color: '#e1931e' }} />,
                duration: 3,
            }
            notification.open(args)
        }
    }
    const onReset = () => {
        formRef.current?.resetFields()
        setMinResourceNeeded(0)
        setMaxResourceNeeded(0)
        setMaxResourceNeededFlag(false)
        setMinResourceNeededFlag(false)
    }
    useEffect(() => {
        if (showSearchPop) {
            console.log('pop searchObj: ', searchObj, formRef.current)
            formRef.current?.setFieldsValue(searchObj)
        }
    }, [showSearchPop])
    return (
        <Drawer title={t('search') + (i18n.language !== 'zh' ? ' ' : '') + t('condition')} placement="right"
            width={600}
            onClose={onClose} visible={showSearchPop}
        >
            <Form {...layout} ref={formRef}
                className='ti_an_detail'
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
            >
                <Form.Item name="techClassId" label={<span className='label'>{t('Classification')}</span>}>
                    <Select style={{ width: '100%' }} allowClear showArrow mode="multiple">
                        {
                            classArr.map(item => (
                                <Option value={item.classId} key={item.classId}
                                    title={item.classificationName}
                                >{item.classificationName}</Option>
                            ))
                        }
                    </Select>
                </Form.Item>
                <Form.Item name="techPtn" label={<span className='label'>{t('PTN')}</span>}>
                    <Input />
                </Form.Item>
                <Form.Item name="researchForms" label={<span className='label'>{t('researchForms')}</span>}>
                    <Select allowClear showArrow mode="multiple">
                        {
                            dicClassList['research form'] && dicClassList['research form'].map(item => (
                                <Option value={item.value} key={item.value}>{item.name}</Option>
                            ))
                        }
                    </Select>
                </Form.Item>
                <Form.Item name="techName" label={<span className='label'>{t('Technology Name')}</span>}>
                    <Input />
                </Form.Item>
                <Form.Item name="subsidiary" label={<span className='label'>{t('subsidiary')}</span>}>
                    <Input />
                </Form.Item>
                <Form.Item name="description" label={<span className='label'>{t('description')}</span>}>
                    <Input />
                </Form.Item>
                <Form.Item name="techLevel" label={<span className='label'>{t('techLevel')}</span>}>
                    <Select allowClear showArrow mode="multiple">
                        <Option value={1}>{t('core')}</Option>
                        <Option value={2}>{t('key')}</Option>
                        <Option value={3}>{t('general')}</Option>
                        <Option value={4}>{t('normal')}</Option>
                    </Select>
                </Form.Item>
                <Form.Item name="priority" label={<span className='label'>{t('priority')}</span>}>
                    <Select allowClear showArrow mode="multiple">
                        {
                            dicClassList['priority'] && dicClassList['priority'].map(item => (
                                <Option value={item.value} key={item.value}>{item.name}</Option>
                            ))
                        }
                    </Select>
                </Form.Item>
                <Form.Item name="quantitativeIndicators" label={<span className='label'>{t('quantitativeIndicators')}</span>}>
                    <Input />
                </Form.Item>
                <Form.Item name="tmtapproval" label={<span className='label'>{t('tmtApproval')}</span>}>
                    <Select allowClear showArrow mode="multiple">
                        {
                            dicClassList['tmt approval'] && dicClassList['tmt approval'].map(item => (
                                <Option value={item.value} key={item.value}>{item.name}</Option>
                            ))
                        }
                    </Select>
                </Form.Item>
                <Form.Item name="maturity" label={<span className='label'>{t('maturity')}</span>}>
                    <Select allowClear showArrow mode="multiple">
                        {
                            dicClassList['maturity'] && dicClassList['maturity'].map(item => (
                                <Option value={item.value} key={item.value}>{item.name}</Option>
                            ))
                        }
                    </Select>
                </Form.Item>
                <Form.Item name="isMajorProject" label={<span className='label'>{t('isMajorProject')}</span>}>
                    <Select allowClear showArrow>
                        <Option value={true}>{t('Yes')}</Option>
                        <Option value={false}>{t('No')}</Option>
                    </Select>
                </Form.Item>
                <Form.Item name="requirementSource" label={<span className='label'>{t('requirementSource')}</span>}>
                    <Select allowClear showArrow mode="multiple">
                        {
                            dicClassList['requirement source'] && dicClassList['requirement source'].map(item => (
                                <Option value={item.value} key={item.value}>{item.name}</Option>
                            ))
                        }
                    </Select>
                </Form.Item>
                <Form.Item name="developedBy" label={<span className='label'>{t('developedBy')}</span>}>
                    <Input />
                </Form.Item>
                <Form.Item name='platformIds'
                    label={<span className='label'>{<span className='label'>{t('product') + (i18n.language !== 'zh' ? ' ' : '') + t('platform')}</span>}</span>}
                >
                    <Select allowClear showArrow showSearch
                        mode='multiple'
                        filterOption={(input, option: any) => {
                            return option.children ? (option.children as string).toLowerCase().includes(input.toLowerCase()) : true
                        }}
                    >
                        {
                            prodList.map(item => (
                                <Option value={item.id} key={item.id}>{item.productPlatform}</Option>
                            ))
                        }
                    </Select>
                </Form.Item>
                <Form.Item name="relevanceBuss" label={<span className='label'>{t('buStakeholder')}</span>}>
                    <Select allowClear showArrow showSearch
                        mode='multiple'
                        filterOption={(input, option) => {
                            return ((option?.value ?? '') + '').toLowerCase().includes(input.toLowerCase())
                        }}
                    >
                        {
                            buList.map(item => (
                                <Option value={item.id} key={item.id}>{item.bu}</Option>
                            ))
                        }
                    </Select>
                </Form.Item>
                <Form.Item name='dutyBuss'
                    label={<span className='label'>{t('developDutySubject')}</span>}
                >
                    <Select allowClear showArrow showSearch
                        mode='multiple'
                        filterOption={(input, option: any) => {
                            return option.children ? (option.children as string).toLowerCase().includes(input.toLowerCase()) : true
                        }}
                    >
                        {
                            buList.map(item => (
                                <Option value={item.id} key={item.id}>{item.bu}</Option>
                            ))
                        }
                    </Select>
                </Form.Item>
                {/* <Form.Item name="planBudgeting"
                    label={<span className='label'>{t('planBudgeting')}</span>} 
                >
                    <InputNumber style={{ width: '100%' }} min={0} step={1} formatter={value => value ? parseInt('' + value) + '' : ''} />
                </Form.Item> */}
                <Form.Item name="planBudgeting" 
                    label={<span className='label'>{t('techPlanCost')}</span>} 
                >
                    <InputNumber style={{ width: '100%' }} min={0} step={1} formatter={value => value ? parseInt('' + value) + '' : ''} />
                </Form.Item>
                <Form.Item name="budgeting" 
                    label={<span className='label'>{t('budgeting')}</span>} 
                >
                    <InputNumber style={{ width: '100%' }} min={0} step={1} formatter={value => value ? parseInt('' + value) + '' : ''} />
                </Form.Item>
                <Form.Item name="toolsEquipment" label={<span className='label'>{t('toolsEquipment')}</span>}>
                    <Input />
                </Form.Item>
                <Form.Item name="isAll" label={<span className='label'>{t('showAll')}</span>}
                    valuePropName="checked"
                >
                    <Switch />
                </Form.Item>
                <Form.Item name="resourceNeeded" label={<span className='label'>{t('resourceNeeded')}</span>}>
                    <Space direction='vertical'>
                        <Space>
                            <Checkbox checked={maxResourceNeededFlag} 
                                onChange={(e: CheckboxChangeEvent) => { setMaxResourceNeededFlag(e.target.checked) }}
                            >&lt;=</Checkbox>
                            <InputNumber min={minResourceNeeded} disabled={!maxResourceNeededFlag} value={maxResourceNeeded} onChange={(e: number | null) =>
                                e ? setMaxResourceNeeded(parseInt('' + (e * 10)) / 10) : 0
                            } />
                        </Space>
                        <Space>
                            <Checkbox checked={minResourceNeededFlag} 
                                onChange={(e: CheckboxChangeEvent) => { setMinResourceNeededFlag(e.target.checked) }}
                            >&gt;=</Checkbox>
                            <InputNumber max={maxResourceNeeded} disabled={!minResourceNeededFlag} value={minResourceNeeded} onChange={(e: number | null) =>
                                e ? setMinResourceNeeded(parseInt('' + (e * 10)) / 10) : 0
                            } />
                        </Space>
                    </Space>
                </Form.Item>
                <Form.Item {...tailLayout} style={{ textAlign: 'center' }}>
                    <Space size={20}>
                        <Button type="primary" htmlType="submit">
                            {t('submit')}
                        </Button>
                        <Button htmlType="button" onClick={onReset}>
                            {t('reset')}
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Drawer>
    )
}

export default Table2SearchPop
